import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNexusAuth } from "@/context/NexusAuthContext";
import { CATEGORIES } from "@/pages/nexus/meta";
import "@/pages/nexus/nexus.css";

const BUDGETS = ["< €100k", "€100k–€500k", "€500k–€2M", "€2M–€10M", "> €10M", "Confidential"];
const TIMEFRAMES = ["< 30 days", "30–90 days", "90–180 days", "> 180 days", "Opportunistic"];

export default function NexusRegister() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/nexus/account";
  const { register } = useNexusAuth() || {};
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", company: "",
    buyer_type: "principal", jurisdictions_text: "", asset_interests: [],
    budget_band: "", timeframe: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggleInterest = (id) => setForm((f) => ({
    ...f,
    asset_interests: f.asset_interests.includes(id) ? f.asset_interests.filter((x) => x !== id) : [...f.asset_interests, id],
  }));

  const BUYER_TYPES = [
    { id: "principal", label: t("nexus.register.buyerPrincipal") },
    { id: "fund",      label: t("nexus.register.buyerFund") },
    { id: "corporate", label: t("nexus.register.buyerCorporate") },
    { id: "broker",    label: t("nexus.register.buyerBroker") },
    { id: "other",     label: t("nexus.register.buyerOther") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({
        ...form,
        jurisdictions: form.jurisdictions_text.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t("nexus.register.welcome"));
      nav(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("nexus.register.registrationFailed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="nexus-register" className="nx-container py-16 max-w-3xl mx-auto">
      <div className="nx-eyebrow mb-3">{t("nexus.register.eyebrow")}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">{t("nexus.register.title")}</h1>
      <p className="text-white/70 mb-8">{t("nexus.register.lead")}</p>

      <form onSubmit={submit} className="space-y-8">
        <div className="grid gap-5 md:grid-cols-2">
          <Field label={`${t("nexus.register.fullName")} *`} name="full_name" required value={form.full_name} onChange={onChange} testid="reg-name" />
          <Field label={t("nexus.register.companyFund")} name="company" value={form.company} onChange={onChange} testid="reg-company" />
          <Field label={`${t("nexus.register.workEmail")} *`} name="email" type="email" required value={form.email} onChange={onChange} testid="reg-email" />
          <Field label={`${t("nexus.register.password")} *`} name="password" type="password" required minLength={8} value={form.password} onChange={onChange} testid="reg-password" />
        </div>

        <div className="nx-h-rule" />

        <div>
          <label className="nx-label">{t("nexus.register.buyerType")} *</label>
          <div className="flex flex-wrap gap-2">
            {BUYER_TYPES.map((bt) => (
              <button
                type="button"
                key={bt.id}
                onClick={() => setForm((f) => ({ ...f, buyer_type: bt.id }))}
                data-testid={`reg-buyertype-${bt.id}`}
                className={`px-4 py-2 rounded-sm font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  form.buyer_type === bt.id
                    ? "bg-[#C9896E] text-[#0d1323]"
                    : "text-white/70 border border-white/15 hover:border-[#C9896E]"
                }`}
              >
                {bt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="nx-label">{t("nexus.register.assetInterest")}</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => toggleInterest(c.id)}
                className={`px-4 py-2 rounded-sm font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  form.asset_interests.includes(c.id)
                    ? "bg-[#C9896E] text-[#0d1323]"
                    : "text-white/70 border border-white/15 hover:border-[#C9896E]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <Field label={t("nexus.register.targetJurisdictions")} name="jurisdictions_text" value={form.jurisdictions_text} onChange={onChange} placeholder={t("nexus.register.targetJurisdictionsPh")} />
          <div>
            <label className="nx-label">{t("nexus.register.budgetBand")}</label>
            <select name="budget_band" value={form.budget_band} onChange={onChange} className="nx-field">
              <option value="">—</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="nx-label">{t("nexus.register.timeframe")}</label>
            <select name="timeframe" value={form.timeframe} onChange={onChange} className="nx-field">
              <option value="">—</option>
              {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>
          <div className="hidden md:block" />
          <div className="md:col-span-2">
            <label className="nx-label">{t("nexus.register.notes")}</label>
            <textarea name="notes" rows={4} maxLength={1500} value={form.notes} onChange={onChange} className="nx-field" placeholder={t("nexus.register.notesPh")} />
          </div>
        </div>

        <button type="submit" className="nx-btn-primary" disabled={busy} data-testid="reg-submit">
          {busy ? t("nexus.register.creating") : <>{t("nexus.register.submit")} <ArrowRight size={14} /></>}
        </button>
        <div className="text-white/60 text-sm">
          {t("nexus.register.already")} <Link to={`/nexus/signin${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-[#C9896E] hover:text-white">{t("nexus.register.signInLink")}</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="nx-label">{label}</label>
      <input className="nx-field" data-testid={testid} {...props} />
    </div>
  );
}
